import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { chatbotApiService } from '../services/chatbotApi';
import { Message } from '../types/message.types';
import { Conversation } from '../types/conversation.types';
import { v4 as uuidv4 } from 'uuid';

interface ChatbotState {
  isOpen: boolean;
  isLoading: boolean;
  isTyping: boolean;
  currentConversation: Conversation | null;
  messages: Message[];
  error: string | null;
  canClose: boolean;
}

interface ChatbotContextType extends ChatbotState {
  openChat: () => void;
  closeChat: () => void;
  sendMessage: (content: string, currentPage?: string) => Promise<void>;
  initConversation: () => Promise<void>;
  clearError: () => void;
}

const ChatbotContext = createContext<ChatbotContextType | undefined>(undefined);

export function ChatbotProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ChatbotState>({
    isOpen: false,
    isLoading: false,
    isTyping: false,
    currentConversation: null,
    messages: [],
    error: null,
    canClose: false,
  });

  const openChat = useCallback(() => {
    setState((s) => ({ ...s, isOpen: true }));
  }, []);

  const closeChat = useCallback(async () => {
    setState((s) => ({ ...s, isOpen: false }));
    
    if (state.currentConversation && state.canClose) {
      try {
        await chatbotApiService.closeConversation(state.currentConversation.id);
      } catch (error) {
        console.error('Erreur fermeture conversation:', error);
      }
    }
  }, [state.currentConversation, state.canClose]);

  const initConversation = useCallback(async () => {
    if (state.currentConversation) return;

    setState((s) => ({ ...s, isLoading: true, error: null }));

    try {
      const res = await chatbotApiService.createConversation();

      const welcomeMessage: Message = {
        id: uuidv4(),
        conversation_id: res.conversation_id,
        type: 'ai',
        content: '👋 Bonjour ! Je suis Iris, votre assistante IA. Comment puis-je vous aider aujourd\'hui ?',
        timestamp: new Date(),
        status: 'sent',
      };

      setState((s) => ({
        ...s,
        currentConversation: {
          id: res.conversation_id,
          title: 'Nouvelle conversation',
          status: 'active',
          created_at: new Date(),
          updated_at: new Date(),
          message_count: 1,
        },
        messages: [welcomeMessage],
        isLoading: false,
      }));
    } catch (error) {
      console.error('Erreur initialisation conversation:', error);
      setState((s) => ({
        ...s,
        isLoading: false,
        error: 'Impossible de démarrer la conversation',
      }));
    }
  }, [state.currentConversation]);

  const sendMessage = useCallback(
    async (content: string, currentPage?: string) => {
      if (!state.currentConversation || !content.trim()) return;

      const conversationId = state.currentConversation.id;

      const userMsg: Message = {
        id: uuidv4(),
        conversation_id: conversationId,
        type: 'user',
        content: content.trim(),
        timestamp: new Date(),
        status: 'sending',
      };

      setState((s) => ({
        ...s,
        messages: [...s.messages, userMsg],
        isTyping: true,
        error: null,
      }));

      try {
        const res = await chatbotApiService.sendMessage({
          conversation_id: conversationId,
          message: content.trim(),
          current_page: currentPage,
        });

        // Update user message status
        setState((s) => ({
          ...s,
          messages: s.messages.map((msg) =>
            msg.id === userMsg.id ? { ...msg, status: 'sent' as const } : msg
          ),
        }));

        const aiMsg: Message = {
          id: uuidv4(),
          conversation_id: conversationId,
          type: 'ai',
          content: res.message,
          timestamp: new Date(),
          status: 'sent',
          navigation: res.navigation,
          metadata: res.metadata,
        };

        setState((s) => ({
          ...s,
          messages: [...s.messages, aiMsg],
          isTyping: false,
          canClose: res.can_close || false,
        }));

        // Navigation manuelle uniquement - pas de redirection automatique
        // L'utilisateur clique sur le bouton dans le message pour naviguer
      } catch (error) {
        console.error('Erreur envoi message:', error);
        setState((s) => ({
          ...s,
          messages: s.messages.map((msg) =>
            msg.id === userMsg.id ? { ...msg, status: 'error' as const } : msg
          ),
          isTyping: false,
          error: 'Impossible d\'envoyer le message',
        }));
      }
    },
    [state.currentConversation]
  );

  const clearError = useCallback(() => {
    setState((s) => ({ ...s, error: null }));
  }, []);

  return (
    <ChatbotContext.Provider
      value={{
        ...state,
        openChat,
        closeChat,
        sendMessage,
        initConversation,
        clearError,
      }}
    >
      {children}
    </ChatbotContext.Provider>
  );
}

export function useChatbot() {
  const context = useContext(ChatbotContext);
  if (context === undefined) {
    throw new Error('useChatbot doit être utilisé dans un ChatbotProvider');
  }
  return context;
}
